import React from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Grid,
} from '@mui/material';
import { LoanRequest, LoanRequestInsurance } from '../../types/authorization';
import { patchInsuranceIdForm } from '../../queries/authorization';
import useStyles, { styles } from './styles';
import AlertDialog from '../AlertDialog';
import { InsuranceType } from '../Simulator/types';
import { getInsuranceForms, getInitialResponses, isCurrentFormComplete } from './helpers';

interface InsuranceFormProps {
  token: string;
  loanRequest: LoanRequest;
  onNext: () => void;
  onError: () => void;
  buttonLabel?: string;
}

const defaultProps = {
  buttonLabel: 'Finalizar',
};

export default function InsuranceForm(props: InsuranceFormProps) {
  const {
    token, loanRequest, onNext, onError, buttonLabel = defaultProps.buttonLabel,
  } = props;

  const classes = useStyles();

  const [insuranceForms, setInsuranceForms] = React.useState<LoanRequestInsurance[]>([]);
  const [formResponses, setFormResponses] = React.useState<Record<string, Record<string, any>>>({});
  const [loading, setLoading] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [markAllAsNo, setMarkAllAsNo] = React.useState(false);
  const [canceledInsurance, setcanceledInsurance] = React.useState<{label: InsuranceType['label'],
    type: InsuranceType['type']}|undefined>(undefined);

  React.useEffect(() => {
    const insurancesWithForms = getInsuranceForms(loanRequest);
    setInsuranceForms(insurancesWithForms);
    const initialResponses = getInitialResponses(insurancesWithForms, loanRequest);
    setFormResponses(initialResponses);
    if (insurancesWithForms.length === 0) {
      onNext();
    }
  }, [loanRequest]);

  const handleFieldValueChange = (insuranceId: string, label: string, value: string) => {
    if (markAllAsNo && value === 'true') {
      setMarkAllAsNo(false);
    }

    setFormResponses((prev) => ({
      ...prev,
      [insuranceId]: {
        ...prev[insuranceId],
        [label]: value,
      },
    }));
  };

  const saveCurrentFormResponses = async (): Promise<boolean> => {
    setLoading(true);

    try {
      const currentInsurance = insuranceForms[activeStep];
      if (!currentInsurance) return false;

      const responses: {label: string, response: boolean}[] = [];

      if (currentInsurance.insurance.formSchema && currentInsurance.insurance.formSchema.fields) {
        currentInsurance.insurance.formSchema.fields.forEach((field) => {
          const value = formResponses[currentInsurance.id][field.label];
          responses.push({
            label: field.label,
            response: field.responseType === 'boolean'
              ? JSON.parse(value)
              : value,
          });
        });
      }

      const res = await patchInsuranceIdForm(token, currentInsurance.insurance.id, responses);
      if (res.status === 206) {
        const cInsurance = insuranceForms
          .find((insurance) => insurance.insurance.type
        === res.data.canceledInsuranceType);
        setcanceledInsurance({
          label: cInsurance?.insurance.label ?? '',
          type: cInsurance?.insurance.type ?? '',
        });
        return false;
      }
      setLoading(false);
      return true;
    } catch (error) {
      onError();
      return false;
    }
  };

  const handleNextForm = async () => {
    if (markAllAsNo) {
      setMarkAllAsNo(false);
    }

    if (activeStep < insuranceForms.length - 1) {
      if (!!canceledInsurance || await saveCurrentFormResponses()) {
        setActiveStep((prevStep) => prevStep + 1);
      }
    } else if (!!canceledInsurance || await saveCurrentFormResponses()) {
      onNext();
    }
  };

  const handleMarkAllAsNo = () => {
    const currentInsurance = insuranceForms[activeStep];
    if (!currentInsurance) return;

    const fields = Object.keys(formResponses[currentInsurance.id] || {});
    setFormResponses((prev) => ({
      ...prev,
      [currentInsurance.id]: fields.reduce((acc, field) => ({
        ...acc,
        [field]: 'false',
      }), {}),
    }));
    setMarkAllAsNo(true);
  };

  const getButtonText = () => {
    if (loading) return 'Guardando...';
    if (activeStep < insuranceForms.length - 1) return 'Siguiente';
    return buttonLabel;
  };

  if (insuranceForms.length === 0) {
    return null;
  }

  const currentInsurance = insuranceForms[activeStep];

  return (
    <Box className={classes.container} sx={{ paddingInline: { md: 4 } }}>
      {currentInsurance && (
        <>
          <Typography variant="h6" gutterBottom textAlign="center">
            {currentInsurance.insurance.formSchema?.label || currentInsurance.insurance.label}
          </Typography>

          <Typography variant="body2" textAlign="center" gutterBottom>
            {currentInsurance.insurance.formSchema?.subLabel
            || currentInsurance.insurance.description}
          </Typography>
          <Button variant="outlined" color="secondary" sx={{ marginBlock: 1, paddingInline: 2, height: '3em' }} onClick={handleMarkAllAsNo}>
            <div className={classes.markAllContainer}>
              <Typography variant="body2" color="primary" sx={{ paddingLeft: 2 }}>
                Marcar NO a todas
              </Typography>
              <Radio sx={{ justifySelf: 'flex-end' }} color="primary" checked={markAllAsNo} disabled />
            </div>
          </Button>
          <Grid
            container
            className={classes.questionsContainer}
            sx={{
              maxHeight: { xs: 285, md: 380 }, md: { marginBlock: 4 },
            }}
          >
            {currentInsurance.insurance.formSchema?.fields
            && currentInsurance.insurance.formSchema.fields.map((field, index) => (
              <Grid
                item
                key={`${field.label}`}
                xs={12}
                sx={{
                  display: 'flex',
                  height: 'min-content',
                  alignItems: 'center',
                  borderTop: '1px solid lightgrey',
                  borderBottom: index === currentInsurance.insurance.formSchema.fields.length - 1 ? '1px solid lightgrey' : 'none',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body2" sx={{ width: { xs: '50%', md: '60%' } }}>
                  {field.label}
                </Typography>
                <RadioGroup
                  value={formResponses[currentInsurance.id]?.[field.label] || ''}
                  onChange={
                    (e) => handleFieldValueChange(currentInsurance.id, field.label, e.target.value)
                  }
                  sx={{
                    width: '40%',
                    ...styles.radioGroup,
                  }}
                >
                  <FormControlLabel
                    value="true"
                    label=""
                    control={(
                      <Radio
                        icon={(
                          <Button variant="outlined" color="secondary" sx={styles.radioButton}>
                            Si
                          </Button>
                        )}
                        checkedIcon={(
                          <Button variant="contained" color="primary" sx={styles.radioButton}>
                            Si
                          </Button>
                        )}
                      />
                    )}
                    sx={{
                      marginRight: { xs: 1, md: 2 },
                    }}
                  />
                  <FormControlLabel
                    value="false"
                    label=""
                    control={(
                      <Radio
                        icon={(
                          <Button variant="outlined" color="secondary" className={classes.radioButton}>
                            No
                          </Button>
                        )}
                        checkedIcon={(
                          <Button variant="contained" color="primary" className={classes.radioButton}>
                            No
                          </Button>
                        )}
                      />
                    )}
                    sx={{
                      marginRight: { xs: 0.5, md: 2 },
                    }}
                  />
                </RadioGroup>
              </Grid>
            ))}
          </Grid>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNextForm}
            disabled={!isCurrentFormComplete(activeStep, insuranceForms, formResponses) || loading}
            sx={{ marginTop: 1 }}
          >
            {getButtonText()}
          </Button>
        </>
      )}
      <AlertDialog
        isTriggered={!!canceledInsurance}
        hideLogo
        maxWidth="sm"
        hideCloseButton
        action={handleNextForm}
        submitButtonLabel="Entendido"
        centerButtons
        pageCall={{
          category: 'LR - CONDICIONES',
          name: 'SEGURO INVALIDADO',
          props: {
            insurance: canceledInsurance?.label || '',
            insuranceType: canceledInsurance?.type || '',
          },
        }}
        dialogTitle={(
          <Typography variant="h6" textAlign="center" component="div" fontWeight="bold">
            Seguro de
            {' '}
            {canceledInsurance?.label}
            {' '}
            deshabilitado
          </Typography>
        )}
        dialogContent={(
          <Typography variant="body2" textAlign="center" gutterBottom>
            Lamentablemente, porque has marcado que posees alguna preexistencia,
            <b>no podemos ofrecerte este seguro.</b>
            {' '}
            Se actualizarán las condiciones de tu crédito, descontando el valor del seguro que
            seleccionaste anteriormente.
          </Typography>
        )}
      />
    </Box>
  );
}

InsuranceForm.defaultProps = defaultProps;
